import axios from 'axios';
import { store } from '../store/store';
import { refreshToken, logoutUser } from '../store/slices/authSlice';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state.auth.accessToken;
    const userRole = state.auth.user?.role;
    const excludedUrls = ['/login', '/refresh'];
    
    if (token && !excludedUrls.some(url => config.url.includes(url))) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Add role-based access control
    if ((userRole === 'carrier' || userRole === 'carrier_admin') && config.url.includes('/api/loads/user')) {
      throw new axios.Cancel('Access denied for carrier role');
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if it's a 401 error
    if (error.response && error.response.status === 401 && !originalRequest._retry) {

      // Exclude the login and refresh token endpoints from interceptor logic
      const excludedUrls = ['/login', '/refresh'];
      if (excludedUrls.some(url => originalRequest.url.includes(url))) {
        // Reject the error without attempting to refresh the token
        return Promise.reject(error);
      }

      // Token refresh logic for other endpoints
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { payload } = await store.dispatch(refreshToken());
        const { access_token } = payload;
        api.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        processQueue(null, access_token);
        isRefreshing = false;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        await store.dispatch(logoutUser());
        isRefreshing = false;
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Add these new EDI-related API functions
export const ediApi = {
  createConfig: (configData) => 
    api.post('/api/edi/configs', configData),
    
  updateConfig: (configId, configData) =>
    api.put(`/api/edi/configs/${configId}`, configData),
    
  testConnection: (configId) =>
    api.post(`/api/edi/configs/${configId}/test`),
    
  listDocuments: (folder, prefix) =>
    api.get(`/api/edi/documents/${folder}`, { params: { prefix } }),
    
  getConfigurations: () =>
    api.get('/api/edi/configs')
};

// Samsara OAuth 2.0 API functions
// Documentation: https://developers.samsara.com/docs/oauth-20
export const samsaraApi = {
  // Initiate OAuth flow - returns authorization URL to redirect user to
  // data: { return_url: string } - URL to redirect back to after OAuth
  initiateOAuth: (data = {}) =>
    api.post('/api/samsara/oauth/initiate', data),
  
  // Refresh the access token (called proactively before expiry)
  refreshToken: () =>
    api.post('/api/samsara/oauth/refresh'),
  
  // Revoke OAuth access and disconnect
  revokeAccess: () =>
    api.post('/api/samsara/oauth/revoke'),
  
  // Get current integration status
  getStatus: () =>
    api.get('/api/samsara/status'),
  
  // Test the connection by making an API call to Samsara
  testConnection: () =>
    api.post('/api/samsara/test'),
  
  // === Fleet Data APIs ===
  
  // Get list of vehicles from Samsara
  getVehicles: () =>
    api.get('/api/samsara/fleet/vehicles'),
  
  // Get current location for a specific vehicle
  getVehicleLocation: (vehicleId) =>
    api.get(`/api/samsara/fleet/vehicles/${vehicleId}/location`),
  
  // Get locations for all fleet vehicles
  getFleetLocations: () =>
    api.get('/api/samsara/fleet/locations'),
  
  // Get Samsara location for a specific load (works for customers too)
  getLoadLocation: (loadId) =>
    api.get(`/api/loads/${loadId}/samsara-location`),
};

// Load tracking source API
export const loadTrackingApi = {
  // Update tracking source for a load
  updateTrackingSource: (loadId, data) =>
    api.put(`/api/loads/${loadId}/tracking-source`, data),
};

// Load Exception API
export const exceptionApi = {
  // Create a new exception for a load
  createException: (loadId, exceptionData) =>
    api.post(`/api/loads/${loadId}/exceptions`, exceptionData),
  
  // Get all exceptions for a load
  getExceptions: (loadId, includeResolved = true) =>
    api.get(`/api/loads/${loadId}/exceptions`, { 
      params: { include_resolved: includeResolved } 
    }),
  
  // Resolve an exception
  resolveException: (loadId, exceptionId, resolutionNotes = '') =>
    api.post(`/api/loads/${loadId}/exceptions/${exceptionId}/resolve`, {
      resolution_notes: resolutionNotes
    }),
  
  // Check for automated exceptions (late arrivals, etc.)
  // Called on page load to detect any new automated exceptions
  checkExceptions: () =>
    api.post('/api/loads/check-exceptions'),
};

export default api;